/**
 * Draft autosave — PURE state machine (PR-ONB-RESUME-1B, lock #4 + review
 * fixes #3/#4/#5 of 2026-06-12).
 *
 * The hook (`useDraftAutosave`) owns timers and API calls; every DECISION
 * lives here so it is unit-testable without React:
 *
 *   - form change → debounce AUTOSAVE_DEBOUNCE_MS, then save;
 *   - step change / "Salva ed esci" → flush that DRAINS the whole queue
 *     (in-flight request + changes typed during it) before reporting done;
 *   - late bind / re-bind (login with the wizard already mounted) →
 *     BIND_SYNCED re-seeds requestPublicId + revision so the first save
 *     never fires a stale expectedRevision (artificial 409);
 *   - 409 DRAFT_REVISION_CONFLICT → NEVER overwrite the server: the machine
 *     enters `conflict`, the hook reloads GET /area/request and re-hydrates;
 *     the user-facing notice (`conflictNotice`) PERSISTS after the technical
 *     state resolves, until dismissed or the user edits again;
 *   - offline → park changes, resume on reconnect.
 *
 * Status vocabulary (UI lock): Salvataggio… / Salvato / Salvataggio non
 * riuscito / Connessione assente (+ persistent conflict banner).
 */

export const AUTOSAVE_DEBOUNCE_MS = 1000;

export type AutosaveStatus =
  | 'idle'
  | 'pending' // change buffered, debounce running
  | 'saving'
  | 'saved'
  | 'error'
  | 'offline'
  | 'conflict';

export interface AutosaveState {
  status: AutosaveStatus;
  /** Request the revision belongs to — a revision never crosses drafts. */
  requestPublicId: string | null;
  /** Server revision to send as expectedRevision on the next save. */
  revision: number;
  /** A change happened after the save currently in flight started. */
  dirtyWhileSaving: boolean;
  lastSavedAt: string | null;
  /**
   * User-facing conflict banner. Independent from `status`: it survives the
   * technical resolution (SERVER_RELOADED) and clears only on explicit
   * dismiss or when the user edits again (review fix #4).
   */
  conflictNotice: boolean;
}

export function initialAutosaveState(
  revision: number,
  requestPublicId: string | null = null,
): AutosaveState {
  return {
    status: 'idle',
    requestPublicId,
    revision,
    dirtyWhileSaving: false,
    lastSavedAt: null,
    conflictNotice: false,
  };
}

export type AutosaveEvent =
  | { type: 'CHANGED' }
  | { type: 'SAVE_STARTED' }
  | { type: 'SAVE_OK'; revision: number; lastSavedAt: string }
  | { type: 'SAVE_CONFLICT'; currentRevision?: number }
  | { type: 'SAVE_FAILED' }
  | { type: 'WENT_OFFLINE' }
  | { type: 'BACK_ONLINE' }
  | { type: 'SERVER_RELOADED'; revision: number }
  | { type: 'BIND_SYNCED'; requestPublicId: string; revision: number }
  | { type: 'CONFLICT_NOTICE_DISMISSED' };

export function autosaveReducer(
  state: AutosaveState,
  event: AutosaveEvent,
): AutosaveState {
  switch (event.type) {
    case 'CHANGED':
      if (state.status === 'offline') return state; // parked until reconnect
      if (state.status === 'conflict') return state; // resolve (reload) first
      if (state.status === 'saving') {
        return { ...state, dirtyWhileSaving: true, conflictNotice: false };
      }
      // Editing again acknowledges a resolved conflict (review fix #4).
      return { ...state, status: 'pending', conflictNotice: false };
    case 'SAVE_STARTED':
      return { ...state, status: 'saving', dirtyWhileSaving: false };
    case 'SAVE_OK': {
      // Changes typed while the request was in flight stay pending —
      // the hook schedules another debounce round.
      const status = state.dirtyWhileSaving ? 'pending' : 'saved';
      return {
        ...state,
        status,
        revision: event.revision,
        dirtyWhileSaving: false,
        lastSavedAt: event.lastSavedAt,
      };
    }
    case 'SAVE_CONFLICT':
      return {
        ...state,
        status: 'conflict',
        conflictNotice: true,
        dirtyWhileSaving: false,
        // The server's revision becomes the truth once the reload lands;
        // keep it now so a retry after reload uses the right expected value.
        revision: event.currentRevision ?? state.revision,
      };
    case 'SAVE_FAILED':
      return { ...state, status: 'error' };
    case 'WENT_OFFLINE':
      return { ...state, status: 'offline' };
    case 'BACK_ONLINE':
      // Anything buffered resumes the normal pending → save path.
      return { ...state, status: 'pending' };
    case 'SERVER_RELOADED':
      // Technical resolution of the conflict; the NOTICE deliberately stays
      // (review fix #4 — the user must be able to perceive what happened).
      return {
        ...state,
        status: 'idle',
        revision: event.revision,
        dirtyWhileSaving: false,
      };
    case 'BIND_SYNCED': {
      // Late bind / re-bind (review fix #3). Same request + same revision →
      // no-op (SAVE_OK already advanced us; do not regress). A DIFFERENT
      // request resets everything: a revision never crosses drafts.
      if (
        state.requestPublicId === event.requestPublicId &&
        state.revision === event.revision
      ) {
        return state;
      }
      if (state.requestPublicId === event.requestPublicId) {
        return { ...state, revision: event.revision };
      }
      return {
        ...initialAutosaveState(event.revision, event.requestPublicId),
      };
    }
    case 'CONFLICT_NOTICE_DISMISSED':
      return { ...state, conflictNotice: false };
  }
}

// ─── Retry policy (review fix 2026-06-12, second round) ─────────────────────
//
//   pending          → automatic autosave (debounce)
//   error            → NO automatic retry: the machine sits still showing
//                      "Salvataggio non riuscito" until the user edits again
//                      (CHANGED → pending) or clicks an EXPLICIT "Riprova"
//   explicit retry   → exactly one attempt per click

/** Gate for the AUTOMATIC debounce path: pending only — never retry errors. */
export function shouldAutosave(state: AutosaveState): boolean {
  return state.status === 'pending';
}

/** Gate for the EXPLICIT user action ("Riprova" / "Salva ed esci"). */
export function canExplicitlyRetry(state: AutosaveState): boolean {
  return state.status === 'pending' || state.status === 'error';
}

/** A flush may declare success: settled on the server, nothing buffered. */
export function isDrained(state: AutosaveState): boolean {
  return (
    !state.dirtyWhileSaving &&
    (state.status === 'idle' || state.status === 'saved')
  );
}

/**
 * Synchronous autosave store (review fix 2026-06-12, second round).
 *
 * React's useReducer state — and any ref synced to it AT RENDER TIME — lags
 * behind dispatches until the next render. An async drain loop that
 * dispatches SAVE_OK and immediately reads the state again would still see
 * the PRE-save snapshot (stale revision → artificial 409; stale 'saving' →
 * false negatives). This store applies the reducer SYNCHRONOUSLY and is the
 * single authoritative state for the async orchestration; the hook forwards
 * every event to React's dispatch purely to refresh the UI.
 */
export interface SyncAutosaveStore {
  getState: () => AutosaveState;
  dispatch: (event: AutosaveEvent) => void;
}

export function createSyncAutosaveStore(
  initial: AutosaveState,
  onEvent?: (event: AutosaveEvent) => void,
): SyncAutosaveStore {
  let current = initial;
  return {
    getState: () => current,
    dispatch: (event) => {
      current = autosaveReducer(current, event);
      onEvent?.(event);
    },
  };
}

/**
 * Drain loop for "Salva ed esci" / step change (review fix #5 + retry
 * policy): keeps saving while there is genuinely buffered work, STOPS AT THE
 * FIRST FAILURE (no burst retries on the same error — the next attempt is
 * the user's explicit "Riprova"), and bails out on offline/conflict. Pure
 * orchestration — the caller injects `getState` (the SYNCHRONOUS store) and
 * `runSave` (awaits one save round including any in-flight request).
 *
 * Returns true ONLY when the final state is clean (saved/idle): the caller
 * must NOT navigate on false and must offer an explicit choice instead.
 */
export async function drainPendingSaves(io: {
  getState: () => AutosaveState;
  runSave: () => Promise<void>;
  maxRounds?: number;
}): Promise<boolean> {
  const maxRounds = io.maxRounds ?? 5;
  for (let round = 0; round < maxRounds; round++) {
    const state = io.getState();
    if (
      state.status === 'offline' ||
      state.status === 'conflict' ||
      state.status === 'error'
    ) {
      return false;
    }
    if (isDrained(state)) return true;
    await io.runSave();
  }
  return isDrained(io.getState());
}

/** UI copy per status (lock #4 vocabulary). Null → render nothing. */
export function autosaveStatusLabel(state: AutosaveState): string | null {
  switch (state.status) {
    case 'saving':
    case 'pending':
      return 'Salvataggio…';
    case 'saved':
      return 'Salvato';
    case 'error':
      return 'Salvataggio non riuscito';
    case 'offline':
      return 'Connessione assente';
    case 'conflict':
      return 'La richiesta è stata aggiornata in un\'altra sessione';
    case 'idle':
      return null;
  }
}
